/**
 * useSyncAvatar — Hook para sincronizar avatar de um contato manualmente.
 * Usa supabase.functions.invoke para auth consistente.
 * Erros são silenciados — falhas de sync são não-críticas.
 */
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';

export function useSyncAvatar() {
    const { user } = useAuth();
    const { currentOrganization } = useOrganization();
    const [isSyncing, setIsSyncing] = useState<Record<string, boolean>>({});

    const syncAvatar = async (
        contactId?: string,
        instanceName?: string,
        options?: { phone?: string; connectionId?: string; force?: boolean }
    ) => {
        if (!user || (!contactId && !options?.phone)) return null;

        const syncKey = contactId || options?.phone || 'unknown';
        setIsSyncing(prev => ({ ...prev, [syncKey]: true }));
        try {
            const { data, error } = await supabase.functions.invoke('sync-contact-avatar', {
                body: {
                    contactId,
                    phone: options?.phone,
                    organizationId: currentOrganization?.id,
                    instanceName,
                    connectionId: options?.connectionId,
                    force: options?.force ?? true,
                }
            });

            if (error) return null;
            return data?.avatar || null;
        } catch {
            return null;
        } finally {
            setIsSyncing(prev => ({ ...prev, [syncKey]: false }));
        }
    };

    return { syncAvatar, isSyncing };
}
